<template>
    <div>
        <div class="flex flex-column gap-2">
            <BaseTextInput v-model="user.name" label="First Name" placeholder="Enter your first name" :errorMessage="formErrors.name" />
            <BaseTextInput v-model="user.surname" label="Last Name" placeholder="Enter your surname" :errorMessage="formErrors.surname" />
            <BaseTextInput v-model="user.email" label="Email" placeholder="Enter your email" :errorMessage="formErrors.email" />

            <!-- If active product is AMS, then show departments -->
            <div v-if="AMSActive && editMode">
                <CustomTreeSelect v-model="selectedDepartment" label="User Department" :optionsTree="treeSelectData" />
            </div>

            <div v-if="authStore.isGodUser" class="field-checkbox">
                <Checkbox v-model="user.avoids_sso_when_active" inputId="bypassSSO" :binary="true" />
                <label for="bypassSSO">Bypass SSO</label>
            </div>
        </div>

        <!-- Add MFA later -->
        <Toolbar>
            <template #end>
                <Button label="Cancel" icon="pi pi-times" class="p-button-danger" @click="cancelOperation" />
                <Button label="Submit" icon="pi pi-check" class="ml-2 p-button-primary" @click="submit" />
            </template>
        </Toolbar>
    </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { storeToRefs } from 'pinia';
import { authStore, userStore, departmentStore, formErrorStore } from '../../../store/global-stores';
import { UserSchema, handleValidation } from '@/utilities/schemas';
import { Products } from '@/enums';

const emit = defineEmits(['close-dialog']);

const { user, editId } = storeToRefs(userStore);
const { formErrors } = storeToRefs(formErrorStore);
const { departmentTree, loggedUserDepartmentTree } = storeToRefs(departmentStore);

const selectedDepartment = ref(null);

// Checks if its editmode or not
const editMode = computed(() => {
    return editId.value ? true : false;
});

// Checks if active product is AMS or PHA
const AMSActive = computed(() => {
    return authStore.selectedProduct === Products.AMS;
});

const treeSelectData = computed(() => {
    return authStore.isGodUser ? departmentTree.value : loggedUserDepartmentTree.value;
});

onMounted(async () => {
    // If its AMS and its edit mode, load departments
    if (editMode.value && AMSActive.value) {
        // Load the departments as per the user who is logged in at the moment
        loadDepartments();
        // Load the information of departments whose data is being edited
        loadSelectedUserDepartments();
    }
});

const loadDepartments = async () => {
    const loggedUserId = authStore.loggedUserId;
    // If root user, load all departments, otherwise load logged user departments only
    authStore.isGodUser ? await departmentStore.fetchAllDepartments() : await departmentStore.fetchLoggedUserDepartments(loggedUserId);
};

const loadSelectedUserDepartments = async () => {
    const userId = editId.value;
    const departmentDetails = await departmentStore.fetchUserDepartments(userId);
    if (departmentDetails && departmentDetails.length > 0) {
        selectedDepartment.value = departmentDetails[0]?.id;
    }
};

const submit = async () => {
    // Check if its edit mode and there is some changes on deparment
    if (editMode.value) {
        // Update department
        await userStore.addUserToDepartments(editId.value, selectedDepartment.value);
    }
    // Validate user form
    if (await handleValidation(UserSchema, user.value)) {
        // If correct then submit
        const userCreated = editMode.value ? await userStore.updateUser() : await userStore.createUser();
        if (userCreated) {
            emit('close-dialog');
        }
    }
};

const cancelOperation = () => {
    // Clean the form information and emit close dialog
    userStore.initForm();
    emit('close-dialog', true);
};
</script>

<style scoped>
.p-toolbar {
    background: none;
    border: none;
}
</style>
